import Phaser from 'phaser';

export class Hat {
    private scene: Phaser.Scene;
    private sprite: Phaser.GameObjects.Image;
    private physicsBody: Phaser.Physics.Arcade.Body;
    private isThrown = false;
    private lifetime = 0;
    private maxLifetime = 5000; // Hat disappears after 5 seconds
    private hasHitMole = false; // Prevent multiple collision detections
    private animationTimer = 0;
    private animationSpeed = 100; // Switch image every 100ms
    private currentFrame = 0;
    private startY = 0; // Starting Y position for scale calculation
    private startScale = 1.0; // Starting scale

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        
        // Create image sprite for the flying sombrero
        // Start with first frame
        this.sprite = scene.add.image(0, 0, 'flyingSombrero01').setDepth(50);
        
        // Keep sprite upright (no rotation)
        this.sprite.setRotation(0);
        
        // Add physics - hat flies off screen without falling
        scene.physics.add.existing(this.sprite);
        this.physicsBody = this.sprite.body as Phaser.Physics.Arcade.Body;
        this.physicsBody.setCircle(20);
        this.physicsBody.setCollideWorldBounds(false); // Don't bounce off walls, fly through
        this.physicsBody.setAllowGravity(false); // No gravity - hat flies straight
        this.physicsBody.setDrag(0, 0); // No drag - maintains velocity
        this.physicsBody.setMaxVelocity(1000, 1000); // Higher max velocity
        this.physicsBody.setFriction(0, 0); // No friction
        
        // Start inactive
        this.sprite.setActive(false);
        this.sprite.setVisible(false);
    }

    throwHat(x: number, y: number, velocityX: number, velocityY: number): void {
        this.sprite.setPosition(x, y);
        this.sprite.setActive(true);
        this.sprite.setVisible(true);
        
        // Keep sprite upright (no rotation)
        this.sprite.setRotation(0);
        
        // Set starting scale and position for perspective effect
        this.startY = y;
        this.startScale = 1.0;
        this.sprite.setScale(this.startScale);
        
        // Start with first frame
        this.currentFrame = 0;
        this.animationTimer = 0;
        this.sprite.setTexture('flyingSombrero01');
        
        this.isThrown = true;
        this.lifetime = 0;
        this.hasHitMole = false; // Reset hit flag
        
        this.physicsBody.setVelocity(velocityX, velocityY);
        this.physicsBody.setAngularVelocity(0); // No rotation - keep sombrero upright
    }

    update(delta: number): void {
        if (this.isThrown && this.sprite.active) {
            this.lifetime += delta;
            this.animationTimer += delta;
            
            // Keep sprite upright (no rotation)
            this.sprite.setRotation(0);
            
            // Cycle between the two sombrero images
            if (this.animationTimer >= this.animationSpeed) {
                this.animationTimer = 0;
                this.currentFrame = (this.currentFrame + 1) % 2;
                const textureName = this.currentFrame === 0 ? 'flyingSombrero01' : 'flyingSombrero02';
                this.sprite.setTexture(textureName);
            }
            
            // Calculate scale based on Y position (smaller as it moves away/up)
            // Hat starts at bottom (higher Y) and moves toward moles (lower Y)
            // As Y decreases (moving up), scale should decrease
            const currentY = this.sprite.y;
            const gameHeight = this.scene.cameras.main.height;
            
            // Calculate distance from starting position
            // The further up (lower Y), the smaller it should be
            const maxDistance = gameHeight * 0.8; // Maximum expected travel distance
            const distanceTraveled = Math.abs(this.startY - currentY);
            
            // Scale from 1.0 (at start) down to 0.3 (at max distance)
            const minScale = 0.3;
            const scaleFactor = 1 - (distanceTraveled / maxDistance);
            const newScale = Math.max(minScale, this.startScale * scaleFactor);
            
            this.sprite.setScale(newScale);
            
            // Remove hat after max lifetime
            if (this.lifetime >= this.maxLifetime) {
                this.resetHat();
                return;
            }
            
            // Remove hat if it goes off screen
            if (this.sprite.x < -50 || this.sprite.x > 410 || 
                this.sprite.y < -50 || this.sprite.y > 690) {
                this.resetHat();
                return;
            }
        }
    }

    resetHat(): void {
        this.isThrown = false;
        this.hasHitMole = false;
        this.sprite.setActive(false);
        this.sprite.setVisible(false);
        this.physicsBody.setVelocity(0, 0);
        this.physicsBody.setAngularVelocity(0);
        this.sprite.setScale(1.0); // Reset scale
        this.animationTimer = 0;
        this.currentFrame = 0;
    }
    
    markAsHit(): void {
        this.hasHitMole = true;
    }
    
    hasHit(): boolean {
        return this.hasHitMole;
    }

    isHatThrown(): boolean {
        return this.isThrown;
    }

    getX(): number {
        return this.sprite.x;
    }

    getY(): number {
        return this.sprite.y;
    }

    getRadius(): number {
        return 20;
    }
    
    getPhysicsBody(): Phaser.Physics.Arcade.Body {
        return this.physicsBody;
    }
}

